import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import FormFader from "../Fader/FormFader";
import Hidden from "@mui/material/Hidden";
import Grid from "@mui/material/Grid";
import { formValue } from "../../../../services/formServiceFunctions";
import MenuItem from "@mui/material/MenuItem";
import FormSubmit from "../FormSubmit/FormSubmit";
import { getCaseReferenceNumberRegEx, getInsuranceNumberRegEx } from "../../../../services/insurerService";
import PersonIcon from "../../../../assets/icons/provision/toggleable/IconPerson";
import FamilyIcon from "../../../../assets/icons/generic/toggleable/IconFamilyDetailed";
import Person from "../Person/Person";
import { useForm } from "../../../../provider/Form/FormProvider";
import { caseReferenceNumberIsUnique, isRequired, matchRegexp } from "../../../../services/validationRules";
import ValidatorTextField from "../../../FormFields/ValidatorTextField";
import Group from "../../../Wizard/Group";
import CheckIcon from "../../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../../assets/icons/common/toggleable/IconClose";
import { getProductData, isInsurancePartnered } from "../../../../services/productService";
import Typography from "@mui/material/Typography";
import FormLegend from "../FormLegend/FormLegend";
import { translate } from "../../../../services/translations/translations";
import NumberFieldConfigured from "../../ConfiguredFormElements/NumberFieldConfigured/NumberFieldConfigured";
import FormSubmitProps from "../../../../types/FormSubmitProps";
import { AbstractCase } from "../../../../types/Entities/AbstractCase";
import wizardStyle from "../../wizardStyle";
import { Box } from "@mui/material";
import ValidatorAutocomplete from "../../../FormFields/ValidatorAutocomplete";
import { sortedInsurances } from "../../../../services/insurancesList";
import InsuranceEntity from "../../../../types/Entities/Insurance";

type InsuranceProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
  withDeductible?: boolean;
  insuranceSubArea?: string;
  forceInsuredOptionHidden?: boolean;
};

export default function Insurance({ submitProps, product, withDeductible = false, insuranceSubArea = "", forceInsuredOptionHidden = false }: InsuranceProps) {
  const { values, handleSubmit, registerValidators, initialValues } = useForm();
  const insuranceNumberRegex = getInsuranceNumberRegEx(values.insurance.insurance);
  const caseReferenceNumberRegex = getCaseReferenceNumberRegEx(values.insurance.insurance);
  const [insuranceNumberRequired, setInsuranceNumberRequired] = useState(true);

  useEffect(() => {
    if (product.insurance.insurancePolicyNumber) {
      // field is hidden if it's already filled
      // a required validator on a hidden field makes submitting the form impossible
      setInsuranceNumberRequired(false);
      return;
    }
    if (product.productCategory === "contractLaw") {
      setInsuranceNumberRequired(true);
      return;
    }
    setInsuranceNumberRequired(values.legalExpenseInsurance === "yes" && !isInsurancePartnered(product));
  }, [values.legalExpenseInsurance]);

  useEffect(() => {
    // if insurance number is not required, clear validators, otherwise use configured validators from field
    if (!insuranceNumberRequired) {
      registerValidators("insurance.insurancePolicyNumber", []);
      return;
    }
  }, [insuranceNumberRegex, insuranceNumberRequired]);

  useEffect(() => {
    if (values.legalExpenseInsurance !== "yes") {
      registerValidators("insurance.caseReferenceNumber", [
        {
          validator: caseReferenceNumberIsUnique,
          params: [initialValues.insurance.caseReferenceNumber],
          message:
            "Diese Schadennummer ist bereits einem anderen Rechtsfall zugeordnet. Bitte überprüfen Sie die Schadennummer. Wenn Sie sich nicht sicher sind, lassen Sie das Feld vorerst leer.",
        },
      ]);
      return;
    }

    if (!isInsurancePartnered(product)) {
      registerValidators("insurance.caseReferenceNumber", [
        {
          validator: matchRegexp,
          params: [caseReferenceNumberRegex],
          message: "Ihre Schadens-/Leistungsnummer hat das falsche Format",
        },
        {
          validator: caseReferenceNumberIsUnique,
          params: [initialValues.insurance.caseReferenceNumber],
          message:
            "Diese Schadennummer ist bereits einem anderen Rechtsfall zugeordnet. Bitte überprüfen Sie die Schadennummer. Wenn Sie sich nicht sicher sind, lassen Sie das Feld vorerst leer.",
        },
      ]);
    }
  }, [caseReferenceNumberRegex, values.legalExpenseInsurance]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Hidden smUp>
            <Typography variant={"h4"} sx={wizardStyle.headline}>
              Versicherung
            </Typography>
          </Hidden>
          <Grid container justifyContent={"center"}>
            {!isInsurancePartnered(product) && !forceInsuredOptionHidden && (
              <Group
                type="radio"
                name="legalExpenseInsurance"
                question={`Sind Sie im Bereich ${getProductData(product.name, "productCategory").label} rechtsschutzversichert?`}
                options={[
                  {
                    labelText: translate("legalExpenseInsurance.options.yes.label", values),
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                  {
                    labelText: translate("legalExpenseInsurance.options.no.label", values),
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                ]}
                validators={[
                  {
                    validator: isRequired,
                    message: "Bitte wählen Sie eine der angezeigten Antworten aus",
                  },
                ]}
              />
            )}
            {(values.legalExpenseInsurance === "yes" || isInsurancePartnered(product)) && (
              <>
                {!isInsurancePartnered(product) && (
                  <>
                    <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                      <FormLegend>{translate("insurance.insurance.question", values)}</FormLegend>
                    </Grid>
                    <Grid item xs={12}>
                      <ValidatorAutocomplete
                        label={translate("insurance.insurance.label", values)}
                        name={"insurance.insurance"}
                        validators={[
                          {
                            validator: isRequired,
                            message: "Bitte geben Sie an, wo Sie rechtsschutzversichert sind.",
                          },
                        ]}
                        helperText={
                          "Bitte wählen Sie Ihren Rechtsschutzversicherer aus." +
                          (insuranceSubArea ? ` Wichtig: Sie benötigen eine Versicherung im Bereich ${insuranceSubArea}.` : "")
                        }
                        options={[
                          { label: "", value: "" },
                          ...sortedInsurances.map((insurance: InsuranceEntity) => ({
                            label: insurance.name,
                            value: "/insurances/" + insurance.id,
                          })),
                        ]}
                        valueOptionAttribute="value"
                        noOptionsText="Kein Versicherer gefunden"
                        renderOption={(props, option) => (
                          <MenuItem {...props} key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        )}
                      />
                    </Grid>
                  </>
                )}
                {(!isInsurancePartnered(product) || !product.insurance.insurancePolicyNumber) && (
                  <>
                    <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                      <FormLegend>{translate("insurance.insurancePolicyNumber.question", values)}</FormLegend>
                    </Grid>
                    <Grid item xs={12}>
                      <ValidatorTextField
                        name="insurance.insurancePolicyNumber"
                        label={translate("insurance.insurancePolicyNumber.label", values)}
                        helperText="Sie finden diese Nummer z.B. auf Ihrem Versicherungsschein oder Ihren Beitragsrechnungen."
                        validators={[
                          {
                            validator: isRequired,
                            message: "Bitte geben Sie Ihre Versichertennummer an.",
                          },
                          {
                            validator: matchRegexp,
                            params: [insuranceNumberRegex],
                            message: "Bitte prüfen Sie die Nummer. Sie finden diese in Ihren Versicherungsunterlagen. Beispiel: 22.6.123.456.8",
                          },
                        ]}
                      />
                    </Grid>
                  </>
                )}
                {!isInsurancePartnered(product) && (
                  <>
                    <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                      <FormLegend>{translate("insurance.caseReferenceNumber.question", values)}</FormLegend>
                    </Grid>
                    <Grid item xs={12}>
                      <ValidatorTextField
                        name="insurance.caseReferenceNumber"
                        label={translate("insurance.caseReferenceNumber.label", values)}
                        helperText="Dies ist die Nummer, die Sie zu Ihrem aktuellen Anliegen erhalten haben. Tragen Sie hier bitte NICHT Ihre Versichertennummer ein."
                        validators={[
                          {
                            validator: matchRegexp,
                            params: [caseReferenceNumberRegex],
                            message: "Ihre Schadens-/Leistungsnummer hat das falsche Format",
                          },
                          {
                            validator: caseReferenceNumberIsUnique,
                            params: [initialValues.insurance.caseReferenceNumber],
                            message:
                              "Diese Schadennummer ist bereits einem anderen Rechtsfall zugeordnet. Bitte überprüfen Sie die Schadennummer. Wenn Sie sich nicht sicher sind, lassen Sie das Feld vorerst leer.",
                          },
                        ]}
                      />
                    </Grid>
                  </>
                )}
                {withDeductible && <NumberFieldConfigured caseData={values} fieldPath={"insurance.deductible"} fieldType={"money"} numberType={"float"} />}
                <Grid item xs={12}>
                  <Group
                    type="radio"
                    name="insurance.policyHolderType"
                    question={translate("insurance.policyHolderType.question", values)}
                    options={[
                      {
                        labelText: translate("insurance.policyHolderType.options.me.label", values),
                        labelIcon: <PersonIcon />,
                        labelIconActive: <PersonIcon active />,
                        value: "me",
                      },
                      {
                        labelText: translate("insurance.policyHolderType.options.familyMember.label", values),
                        labelIcon: <FamilyIcon />,
                        labelIconActive: <FamilyIcon active />,
                        value: "familyMember",
                      },
                    ]}
                    validators={[
                      {
                        validator: isRequired,
                        message: "Bitte wählen Sie eine der angezeigten Antworten aus",
                      },
                    ]}
                  />
                </Grid>
                {formValue(values, "insurance.policyHolderType") === "familyMember" && (
                  <Person path={"insurance.policyHolder"} headline={"Wie heißt die Person, auf die die Rechtsschutz-Versicherung läuft?"} />
                )}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
